import { createGlobalTheme, globalStyle, style } from '@vanilla-extract/css';


export const vars = createGlobalTheme(':root', {
  color: {
    canvas: '#f1eee5',
    paper: '#fbfaf5',
    paperStrong: '#ffffff',
    ink: '#181816',
    inkSoft: '#45453f',
    muted: '#77766d',
    faint: '#aaa89d',
    line: '#d8d4c8',
    lineStrong: '#b9b5a8',
    cobalt: '#2854c5',
    cobaltSoft: '#e8edff',
    vermilion: '#d54a2f',
    vermilionSoft: '#fff0eb',
    green: '#147a5b',
    greenSoft: '#e3f4ed',
    amber: '#9b6618',
    amberSoft: '#fff4d9',
    danger: '#b12b3d',
    dangerSoft: '#fdecef',
    code: '#15171b',
    codeInk: '#f4f1e8'
  },
  font: {
    ui: '"Instrument Sans Variable", "Instrument Sans", system-ui, sans-serif',
    reading: '"Source Serif 4 Variable", "Source Serif 4", Georgia, serif',
    mono: '"JetBrains Mono Variable", "JetBrains Mono", monospace'
  },
  space: {
    x1: '4px',
    x2: '8px',
    x3: '12px',
    x4: '16px',
    x6: '24px',
    x8: '32px',
    x12: '48px',
    x16: '64px'
  },
  radius: {
    small: '6px',
    medium: '10px',
    large: '16px',
    round: '999px'
  },
  shadow: {
    low: '0 1px 2px rgba(24, 24, 22, .05)',
    medium: '0 18px 50px rgba(40, 36, 24, .10)'
  }
});


createGlobalTheme('[data-theme="dusk"]', vars, {
  color: {
    canvas: '#171512',
    paper: '#1e1c18',
    paperStrong: '#252218',
    ink: '#ece7db',
    inkSoft: '#c4bfb2',
    muted: '#98937f',
    faint: '#6e6a5c',
    line: '#333029',
    lineStrong: '#48443a',
    cobalt: '#8fb0ff',
    cobaltSoft: '#232c42',
    vermilion: '#ff8a6b',
    vermilionSoft: '#33201a',
    green: '#6fd0ab',
    greenSoft: '#15281f',
    amber: '#e2b96a',
    amberSoft: '#2b2312',
    danger: '#ff7585',
    dangerSoft: '#331218',
    code: '#121110',
    codeInk: '#ddd8cc'
  },
  font: {
    ui: '"Instrument Sans Variable", "Instrument Sans", system-ui, sans-serif',
    reading: '"Source Serif 4 Variable", "Source Serif 4", Georgia, serif',
    mono: '"JetBrains Mono Variable", "JetBrains Mono", monospace'
  },
  space: {
    x1: '4px', x2: '8px', x3: '12px', x4: '16px', x6: '24px', x8: '32px', x12: '48px', x16: '64px'
  },
  radius: { small: '6px', medium: '10px', large: '16px', round: '999px' },
  shadow: { low: '0 1px 2px rgba(0,0,0,.4)', medium: '0 18px 50px rgba(0,0,0,.7)' }
});

createGlobalTheme('[data-theme="oled"]', vars, {
  color: {
    canvas: '#000000',
    paper: '#050505',
    paperStrong: '#0a0a0a',
    ink: '#e9e8e2',
    inkSoft: '#bebdb7',
    muted: '#8b8a85',
    faint: '#62625e',
    line: '#242424',
    lineStrong: '#383838',
    cobalt: '#82aaff',
    cobaltSoft: '#101a2d',
    vermilion: '#ff8068',
    vermilionSoft: '#29130f',
    green: '#65c9a5',
    greenSoft: '#0d211a',
    amber: '#e5b567',
    amberSoft: '#241b0d',
    danger: '#ff6b7a',
    dangerSoft: '#2a1015',
    code: '#000000',
    codeInk: '#d8d8d2'
  },
  font: {
    ui: '"Instrument Sans Variable", "Instrument Sans", system-ui, sans-serif',
    reading: '"Source Serif 4 Variable", "Source Serif 4", Georgia, serif',
    mono: '"JetBrains Mono Variable", "JetBrains Mono", monospace'
  },
  space: {
    x1: '4px', x2: '8px', x3: '12px', x4: '16px', x6: '24px', x8: '32px', x12: '48px', x16: '64px'
  },
  radius: { small: '6px', medium: '10px', large: '16px', round: '999px' },
  shadow: { low: '0 1px 2px rgba(0,0,0,.3)', medium: '0 18px 50px rgba(0,0,0,.65)' }
});


globalStyle('*', { boxSizing: 'border-box' });
globalStyle('html', {
  // Prevent mobile browsers from inflating the layout viewport on text-heavy
  // pages, which silently halves the CSS px width and breaks overflow checks.
  WebkitTextSizeAdjust: '100%',
  MozTextSizeAdjust: '100%',
  textSizeAdjust: '100%'
});
globalStyle('html', { background: vars.color.canvas, color: vars.color.ink, fontFamily: vars.font.ui, scrollBehavior: 'smooth' });
globalStyle('body', { margin: 0, minWidth: 320, minHeight: '100vh', background: vars.color.canvas });
globalStyle('button, input, textarea, select', { font: 'inherit' });
globalStyle('button, a', { WebkitTapHighlightColor: 'transparent' });
globalStyle('a', { color: 'inherit', textDecoration: 'none' });
globalStyle('::selection', { background: vars.color.cobaltSoft, color: vars.color.ink });
globalStyle(':focus-visible', { outline: `3px solid ${vars.color.cobalt}`, outlineOffset: '3px' });

export const button = style({
  minHeight: 40,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: vars.space.x2,
  padding: `0 ${vars.space.x4}`,
  border: `1px solid ${vars.color.lineStrong}`,
  borderRadius: vars.radius.medium,
  background: vars.color.paperStrong,
  color: vars.color.ink,
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'background 140ms ease, border-color 140ms ease, transform 140ms ease',
  selectors: {
    '&:hover': { borderColor: vars.color.inkSoft, background: vars.color.paper },
    '&:active': { transform: 'translateY(1px)' },
    '&:disabled': { cursor: 'not-allowed', opacity: .45 }
  }
});

export const primaryButton = style({
  background: vars.color.ink,
  borderColor: vars.color.ink,
  color: vars.color.paper,
  selectors: { '&:hover': { background: vars.color.cobalt, borderColor: vars.color.cobalt } }
});

export const quietButton = style({
  background: 'transparent',
  borderColor: 'transparent',
  selectors: { '&:hover': { background: '#e8e4d9', borderColor: '#e8e4d9' } }
});

export const iconButton = style({ width: 40, padding: 0 });

export const panel = style({
  background: vars.color.paper,
  border: `1px solid ${vars.color.line}`,
  borderRadius: vars.radius.large,
  boxShadow: vars.shadow.low
});

export const eyebrow = style({
  margin: 0,
  color: vars.color.muted,
  fontFamily: vars.font.mono,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.12em',
  textTransform: 'uppercase'
});

export const statusBadge = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 26,
  padding: '0 9px',
  borderRadius: vars.radius.round,
  border: `1px solid ${vars.color.line}`,
  color: vars.color.inkSoft,
  background: vars.color.paperStrong,
  fontFamily: vars.font.mono,
  fontSize: 11,
  fontWeight: 700
});
